import { format } from "date-fns";
import { Tables } from "@/integrations/supabase/types";

interface SupplierQuoteRequestDocumentProps {
  quoteRequest: Tables<"supplier_quote_requests"> & {
    leads: Tables<"leads">;
    suppliers: Tables<"suppliers">;
    itinerary_items?: Tables<"itinerary_items"> | null;
    profiles: Tables<"profiles">;
  };
  brandLogo?: string;
}

export function SupplierQuoteRequestDocument({ 
  quoteRequest,
  brandLogo 
}: SupplierQuoteRequestDocumentProps) {
  const { leads: lead, suppliers: supplier, itinerary_items: item, request_type, profiles } = quoteRequest;
  
  // Parse itinerary snapshot for full itinerary requests
  const itinerarySnapshot = quoteRequest.itinerary_snapshot as any[] | null;

  return (
    <div className="bg-white text-foreground p-8 max-w-4xl mx-auto space-y-6">
      {/* Header with Logo */}
      <div className="flex items-start justify-between border-b pb-6">
        <div className="flex-1">
          {brandLogo ? (
            <img 
              src={brandLogo} 
              alt="Brand Logo" 
              className="h-16 object-contain"
              style={{ mixBlendMode: 'multiply' }}
            />
          ) : (
            <div className="text-2xl font-bold text-primary">
              {lead.brand || "Creative Travel Africa"}
            </div>
          )}
        </div>
        <div className="text-right">
          <h1 className="text-xl font-bold text-foreground">
            {request_type === 'single_item' ? 'Item Quote Request' : 'Full Itinerary Quote Request'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Request Date: {format(new Date(quoteRequest.request_date), "dd MMM yyyy")}
          </p>
        </div>
      </div>

      {/* Supplier Details */}
      <div className="bg-muted/30 p-4 rounded-lg">
        <h2 className="font-semibold text-sm text-muted-foreground mb-2">TO:</h2>
        <p className="font-bold text-lg">{supplier.name}</p>
        {supplier.contact_name && <p className="text-sm">{supplier.contact_name}</p>}
        {supplier.email && <p className="text-sm text-muted-foreground">{supplier.email}</p>}
      </div>

      {/* Request Overview */}
      <div className="space-y-3">
        <h2 className="font-bold text-lg border-b pb-2">REQUEST DETAILS</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Lead Reference:</span>
            <span className="ml-2 font-semibold">{lead.id.slice(0, 8).toUpperCase()}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Pax Count:</span>
            <span className="ml-2 font-semibold">{lead.pax_count || 'TBC'}</span>
          </div>
          {lead.start_date && lead.end_date && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Travel Dates:</span>
              <span className="ml-2 font-semibold">
                {format(new Date(lead.start_date), "dd MMM yyyy")} - {format(new Date(lead.end_date), "dd MMM yyyy")}
              </span>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Requested By:</span>
            <span className="ml-2 font-semibold">{profiles.full_name || profiles.email}</span>
          </div>
          {quoteRequest.expires_at && (
            <div>
              <span className="text-muted-foreground font-bold text-destructive">Response Deadline:</span>
              <span className="ml-2 font-bold text-destructive">{format(new Date(quoteRequest.expires_at), "dd MMM yyyy")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Single Item Mode */}
      {request_type === 'single_item' && item && (
        <div className="space-y-4">
          <h2 className="font-bold text-lg border-b pb-2">ITEM DETAILS</h2>
          <div className="border rounded-lg p-4 space-y-3">
            {item.manual_image_url && (
              <img 
                src={item.manual_image_url} 
                alt={item.title}
                className="w-full h-48 object-cover rounded"
              />
            )}
            <div>
              <h3 className="font-bold text-lg">{item.custom_title || item.title}</h3>
              <p className="text-sm text-muted-foreground capitalize">{item.item_type.replace('_', ' ')}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              {item.date && (
                <div>
                  <span className="text-muted-foreground">Date:</span>
                  <span className="ml-2 font-semibold">{format(new Date(item.date), "dd MMM yyyy")}</span>
                </div>
              )}
              {item.location && (
                <div>
                  <span className="text-muted-foreground">Location:</span>
                  <span className="ml-2 font-semibold">{item.location}</span>
                </div>
              )}
              {item.quantity && (
                <div>
                  <span className="text-muted-foreground">Quantity:</span>
                  <span className="ml-2 font-semibold">{item.quantity}</span>
                </div>
              )}
              {item.check_in && item.check_out && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Check-in/out:</span>
                  <span className="ml-2 font-semibold">
                    {format(new Date(item.check_in), "dd MMM")} - {format(new Date(item.check_out), "dd MMM yyyy")}
                  </span>
                </div>
              )}
            </div>

            {(item.custom_description || item.description) && (
              <div>
                <p className="text-muted-foreground text-sm mb-1">Description:</p>
                <p className="text-sm whitespace-pre-wrap">{item.custom_description || item.description}</p>
              </div>
            )}

            {item.notes && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                <p className="text-xs text-muted-foreground mb-1">Special Requirements:</p>
                <p className="text-sm">{item.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Itinerary Mode */}
      {request_type === 'full_itinerary' && itinerarySnapshot && (
        <div className="space-y-4">
          <h2 className="font-bold text-lg border-b pb-2">FULL ITINERARY</h2>
          
          {/* Programme Overview */}
          <div className="bg-muted/30 p-4 rounded-lg space-y-2">
            <h3 className="font-semibold">Programme Overview:</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {lead.destination && (
                <div>
                  <span className="text-muted-foreground">Destination:</span>
                  <span className="ml-2 font-semibold">{lead.destination}</span>
                </div>
              )}
              {lead.start_date && lead.end_date && (
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="ml-2 font-semibold">
                    {Math.ceil((new Date(lead.end_date).getTime() - new Date(lead.start_date).getTime()) / (1000 * 60 * 60 * 24))} days
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Itinerary Items */}
          <div className="space-y-4">
            {itinerarySnapshot.map((item: any, index: number) => (
              <div key={item.id || index} className="border-l-4 border-primary/30 pl-4 py-2 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-bold">{item.custom_title || item.title}</h4>
                    <p className="text-xs text-muted-foreground capitalize">{item.item_type?.replace('_', ' ')}</p>
                  </div>
                  {item.date && (
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(item.date), "dd MMM")}
                    </span>
                  )}
                </div>
                
                {item.location && (
                  <p className="text-sm"><span className="text-muted-foreground">Location:</span> {item.location}</p>
                )}
                
                {(item.custom_description || item.description) && (
                  <p className="text-sm text-muted-foreground">{item.custom_description || item.description}</p>
                )}

                {item.notes && (
                  <p className="text-xs bg-yellow-50 p-2 rounded border-l-2 border-yellow-400">
                    <span className="font-semibold">Note:</span> {item.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Consultant Notes */}
      {quoteRequest.notes_to_supplier && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <h3 className="font-semibold text-sm mb-2">SCOPE OF QUOTE REQUESTED:</h3>
          <p className="text-sm whitespace-pre-wrap">{quoteRequest.notes_to_supplier}</p>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-muted/50 p-4 rounded-lg space-y-2">
        <h3 className="font-semibold">Instructions for Supplier:</h3>
        <p className="text-sm text-muted-foreground">Please provide your quote including:</p>
        <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1 ml-2">
          <li>Detailed pricing breakdown</li>
          <li>What's included and excluded</li>
          <li>Terms and conditions</li>
          <li>Availability confirmation</li>
        </ul>
      </div>

      {/* Footer */}
      <div className="border-t pt-4 text-center text-sm text-muted-foreground">
        <p>For questions or to submit your quote, please contact:</p>
        <p className="font-semibold text-foreground mt-1">{profiles.email}</p>
      </div>
    </div>
  );
}
